import { ChessSets } from "@chesshotel/constants";
import Piece from "./Piece";
import { Z_FADE_OUT, Z_OVER, Z_UNDER } from "./animations";
import React, { memo, useEffect, useRef } from "react";
import { Board as AnimatedBoard, Piece as AnimatedPiece } from "./types";

interface PieceData {
  id: string;
  color: "white" | "black";
  role: "king" | "queen" | "rook" | "bishop" | "knight" | "pawn";
}

interface PiecesProps {
  position: Record<string, PieceData | AnimatedPiece | undefined>;
  freeMove: boolean;
  fadeOuts?: AnimatedPiece[];
  pieceRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  playingWhite: boolean;
  animate?: boolean;
  moves?: Record<string, { id: string }>;
  chessSet?: ChessSets;
  onAnimationEnd?: (() => void) | null;
  boardSize: number;
}

function Pieces({
  position,
  freeMove,
  // newPieces,
  fadeOuts = [],
  pieceRefs,
  playingWhite,
  animate = false,
  moves = {},
  chessSet = ChessSets.CHESSHOTEL,
  onAnimationEnd = null,
  boardSize,
}: PiecesProps) {
  // Track previous piece IDs to clean up stale refs
  const prevPieceIdsRef = useRef<Set<string>>(new Set());

  function renderPiece(piece: PieceData, key: string, zIndex: number) {
    return (
      <Piece
        key={piece.id}
        piece={piece}
        currentKey={key}
        pieceRefs={pieceRefs}
        playingWhite={playingWhite}
        animate={animate}
        zIndex={zIndex}
        chessSet={chessSet}
        canDrag={
          freeMove ||
          (playingWhite && piece.color === "white") ||
          (!playingWhite && piece.color === "black")
        }
        onAnimationEnd={onAnimationEnd}
        boardSize={boardSize}
      />
    );
  }

  const pieces = [];
  const currentPieceIds = new Set<string>();

  for (let key in position) {
    const piece = position[key];
    if (piece) {
      const typed = piece as PieceData;
      currentPieceIds.add(typed.id);
      const zIndex = moves[typed.id] ? Z_OVER : Z_UNDER;
      pieces.push(renderPiece(typed, key, zIndex));
    }
  }

  fadeOuts.forEach((piece) => {
    if (piece) {
      currentPieceIds.add(piece.id);
      pieces.push(renderPiece(piece, piece.key, Z_FADE_OUT));
    }
  });

  // Clean up stale pieceRefs for pieces that are no longer on the board
  useEffect(() => {
    const prevIds = prevPieceIdsRef.current;
    prevIds.forEach((id) => {
      if (!currentPieceIds.has(id) && pieceRefs.current[id]) {
        delete pieceRefs.current[id];
      }
    });
    prevPieceIdsRef.current = currentPieceIds;
  });

  return <>{pieces}</>;
}

export default memo(Pieces);
